import { useToaster } from '@innovationdepartment/proxima-ui';
import { useShowSpinner } from 'hooks';
import useBrandTagsApi from 'hooks/useBrandTagsApi';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import AddNewCategoryModal from './AddNewCategoryModal';
import CreateBrandTagView from './CreateBrandTag.View';

const ADD_NEW_CATEGORY = '__ADD_NEW_CATEGORY__';
const ADD_NEW_SUBCATEGORY = '__ADD_NEW_SUBCATEGORY__';

const INITIAL_FORM_DATA = {
  displayName: '',
  description: '',
  category: 'aesthetic',
  subCategory: '',
  genderRelevance: 'unisex',
  typeDisplayName: '',
  typeHelperText: '',
  brandAssignmentGuidance: '',
  personaRelevanceGuidance: '',
  personaEligible: true,
  sensitive: false,
  deprecated: false
};

const CreateBrandTagContainer = () => {
  const navigate = useNavigate();
  const { showToaster } = useToaster();
  const { createTag, getCategories, getSubCategories, getGenders, loading } = useBrandTagsApi();

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [categories, setCategories] = useState<string[]>([]);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [showModal, setShowModal] = useState<'category' | 'subcategory' | null>(null);

  useShowSpinner({ show: loading });

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    const loadAllData = async () => {
      try {
        const [cats, genderOptions] = await Promise.all([getCategories(), getGenders()]);

        setCategories(cats);
        setGenders(genderOptions);
        setDataLoaded(true);
      } catch {
        showToaster({
          message: 'Failed to load form data',
          variant: 'error'
        });
        navigate('/brand-tags');
      }
    };

    loadAllData();
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-Biome migration; the app is already working as expected with these dependency arrays
  useEffect(() => {
    if (!formData.category) {
      return;
    }

    const loadSubCategoriesForCategory = async () => {
      try {
        const subCats = await getSubCategories(formData.category);
        setSubCategories(subCats);
      } catch {
        showToaster({
          message: 'Failed to load subcategories',
          variant: 'error'
        });
      }
    };

    loadSubCategoriesForCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category]);

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      [field]: value
    });

    if (value) {
      setErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(field);
        return newErrors;
      });
    }
  };

  const handleCategoryChange = async (value: string) => {
    if (value === ADD_NEW_CATEGORY) {
      setShowModal('category');
    } else {
      setFormData({
        ...formData,
        category: value,
        subCategory: '' // Reset subcategory when category changes
      });

      // Clear errors for category and subcategory
      if (value) {
        setErrors(prev => {
          const newErrors = new Set(prev);
          newErrors.delete('category');
          return newErrors;
        });
      }
    }
  };

  const handleSubCategoryChange = async (value: string) => {
    if (value === ADD_NEW_SUBCATEGORY) {
      setShowModal('subcategory');
    } else {
      setFormData({
        ...formData,
        subCategory: value
      });

      // Clear error for subcategory
      if (value) {
        setErrors(prev => {
          const newErrors = new Set(prev);
          newErrors.delete('subCategory');
          return newErrors;
        });
      }
    }
  };

  const handleModalSubmit = (newValue: string) => {
    if (showModal === 'category') {
      setCategories([...categories, newValue]);
      setFormData({
        ...formData,
        category: newValue,
        subCategory: '' // Reset subcategory when category changes
      });
      // Clear category error
      setErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete('category');
        return newErrors;
      });
    } else if (showModal === 'subcategory') {
      setSubCategories([...subCategories, newValue]);
      setFormData({
        ...formData,
        subCategory: newValue
      });
      // Clear subcategory error
      setErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete('subCategory');
        return newErrors;
      });
    }

    setShowModal(null);
  };

  const handleModalCancel = () => {
    setShowModal(null);
  };

  const validateForm = (): boolean => {
    const newErrors = new Set<string>();

    const requiredFields = [
      'displayName',
      'category',
      'description',
      'subCategory',
      'genderRelevance',
      'typeDisplayName',
      'typeHelperText'
    ];

    requiredFields.forEach(field => {
      const value = formData[field as keyof typeof formData];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        newErrors.add(field);
      }
    });

    setErrors(newErrors);
    return newErrors.size === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) {
      showToaster({
        message: 'Please fill in all required fields',
        variant: 'error'
      });
      return;
    }

    const tag = await createTag(formData);

    if (!tag) {
      showToaster({
        message: 'Failed to create tag',
        variant: 'error'
      });
      return;
    }

    showToaster({
      message: 'Tag created successfully',
      variant: 'success'
    });

    navigate(`/brand-tags/${tag.id}`);
  };

  const handleBack = () => {
    navigate('/brand-tags');
  };

  const categoryOptions = useMemo(
    () => [
      ...categories.map(cat => ({ label: cat, value: cat })),
      { label: '+ Add New Category', value: ADD_NEW_CATEGORY }
    ],
    [categories]
  );

  const subCategoryOptions = useMemo(
    () => [
      ...subCategories.filter(sub => sub !== '').map(sub => ({ label: sub, value: sub })),
      { label: '+ Add New Sub-Category', value: ADD_NEW_SUBCATEGORY }
    ],
    [subCategories]
  );

  const genderOptions = useMemo(
    () => genders.map(gender => ({ label: gender, value: gender })),
    [genders]
  );

  return (
    <>
      <CreateBrandTagView
        formData={formData}
        errors={errors}
        onFieldChange={handleFieldChange}
        onBack={handleBack}
        onCreate={handleCreate}
        categoryOptions={categoryOptions}
        subCategoryOptions={subCategoryOptions}
        genderOptions={genderOptions}
        onCategoryChange={handleCategoryChange}
        onSubCategoryChange={handleSubCategoryChange}
        dataLoaded={dataLoaded}
      />
      <AddNewCategoryModal
        show={showModal !== null}
        type={showModal || 'category'}
        onCancel={handleModalCancel}
        onSubmit={handleModalSubmit}
      />
    </>
  );
};

export default CreateBrandTagContainer;
